import { zodResolver } from '@hookform/resolvers/zod';
import {
  queryOptions,
  useMutation,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import { languageOptions } from '@constants/languages.constant';
import { queryKeys } from '@constants/queryKeys.constant';
import { ESmartEventTimelineKeyType } from '@enums/smartEvent.enum';
import { EStaleTime } from '@enums/staleTime.enum';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import { invalidateSmartEventTimeline } from '@helpers/queryCache/invalidateSmartEventTimeline.helper';
import { updatePartnershipInList } from '@helpers/queryCache/updatePartnershipInList.helper';
import {
  updatePartnershipSchema,
  type UpdatePartnershipDto,
  type UpdatePartnershipPayload,
} from '@services/partnerships/dtos/update.dto';
import { partnershipService } from '@services/partnerships/service';
import type { GetPartnershipByIdOutput } from '@services/partnerships/types/getById.type';
import type { UpdatePartnershipOutput } from '@services/partnerships/types/update.type';

import { PostcodesField } from './@components/Postcodes/Field';
import { ServicesField } from './@components/Services/Field';

function partnershipQueryOptions(id: string) {
  return queryOptions({
    queryKey: queryKeys.partnerships.detail(id),
    queryFn: () => partnershipService.getById(id),
    staleTime: EStaleTime.OneHour,
  });
}

export const Route = createFileRoute(
  '/_layout/(private)/providers/$id/partnerships/$partnershipId/update',
)({
  loader: ({ context, params }) => {
    const options = partnershipQueryOptions(params.partnershipId);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
  pendingComponent: () => (
    <div className="flex min-h-dvh w-full items-center justify-center">
      <LoaderCircle className="text-primary animate-spin" size={80} />
    </div>
  ),
  pendingMs: 300,
  pendingMinMs: 600,
  component: ProviderPartnershipUpdate,
});

function ProviderPartnershipUpdate() {
  const { queryClient } = Route.useRouteContext();
  const { id: providerId, partnershipId } = Route.useParams();

  const navigate = Route.useNavigate();

  const { data } = useSuspenseQuery(partnershipQueryOptions(partnershipId));

  const form = useForm<UpdatePartnershipDto>({
    resolver: zodResolver(updatePartnershipSchema),
    mode: 'onBlur',
    shouldUnregister: true,
    defaultValues: {
      acceptsVipRequests: data.partnership.acceptsVipRequests,
      coveredLanguages: data.partnership.coveredLanguages,
      coveredPostcodes: data.partnership.coveredPostcodes,
      name: data.partnership.name,
      services: data.partnership.services,
    },
  });

  const { isPending, mutate: updatePartnership } = useMutation<
    UpdatePartnershipOutput,
    unknown,
    UpdatePartnershipPayload
  >({
    mutationFn: partnershipService.update,
    onError: handleRequestError,
    onSuccess: async ({ partnership }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.partnerships.detail(partnershipId),
      });

      queryClient.setQueryData<GetPartnershipByIdOutput>(
        queryKeys.partnerships.detail(partnershipId),
        { partnership },
      );

      await updatePartnershipInList({
        partnershipId,
        providerId,
        queryClient,
        update: current => ({ ...current, ...partnership }),
      });

      invalidateSmartEventTimeline({
        id: providerId,
        queryClient,
        type: ESmartEventTimelineKeyType.Provider,
      });

      navigate({
        to: '/providers/$id',
        params: { id: providerId },
        replace: true,
      });
      toast.success('Success', {
        description: 'Partnership updated successfully.',
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    updatePartnership({ ...values, id: partnershipId });
  });

  return (
    <Form.Provider {...form}>
      <PageTitle
        hasBackButton
        params={{ id: providerId }}
        title="Update partnership"
        to="/providers/$id"
      />

      <form className="space-y-2" onSubmit={handleSubmit}>
        <InputControlled label="Name" name="name" />

        <ServicesField businessLineId={data.partnership.businessLineId} />
        <PostcodesField />

        <Separator variant="form" />

        <div className="grid h-fit items-start gap-2.5 sm:grid-cols-3">
          <InputControlled
            label="Covered languages"
            name="coveredLanguages"
            options={languageOptions}
            variant="checkboxGroup"
          />
          <InputControlled
            name="acceptsVipRequests"
            title="Accepts VIP requests"
            variant="switch"
          />
        </div>

        {!data.partnership.archived && (
          <div className="mt-10 flex justify-center gap-x-2">
            <Anchor
              className="min-w-40 uppercase"
              disabled={isPending}
              params={{ id: providerId }}
              rounded="lg"
              size="lg"
              to="/providers/$id"
              variant="destructive"
            >
              Cancel
            </Anchor>
            <Button
              className="min-w-52"
              isLoading={isPending}
              size="lg"
              type="submit"
            >
              Save
            </Button>
          </div>
        )}
      </form>
    </Form.Provider>
  );
}
